import React, { useEffect, useState } from 'react';
import { Image } from 'antd';
import { useParams } from 'react-router-dom';
import { isFunctionalityEnabled } from '../../../../config/functionalityAvailability';
import { UPLOAD_PHOTOS } from '../../../../config/paramsConfig';
import { NavigationKey } from '../../../components/appNavigationBar/AppNavigation.types';
import PageHeader from '../../../components/common/pageHeader/PageHeader';
import ProductItemHeader from '../../../components/favoritProducts/favoriteProductItem/productItemHeader/ProductItemHeader';
import ProductComments from '../../../components/productInfo/comments/ProductComments';
import ProductUploads, { UploadType } from '../../../components/productInfo/uploads/UploadsInfo';
import { flattenTcMfSectionProducts } from '../../../helpers/tcExternalProducts';
import { toGuidelineInternalProduct } from '../../../helpers/liveGuidelineProducts';
import { mergeInternalProductsWithExternal } from '../../../helpers/mergeProjectProducts';
import HelpModal from '../../../components/helpModal/HelpModal';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useModalState } from '../../../hooks/modalState';
import { useUploadDocumentTypes } from '../../../hooks/uploadDocumentTypes';
import { ActionResponse } from '../../../models/actionResponse/types';
import { AccountPermissionTypes, ProjectPermissionTypes } from '../../../models/permission/enums';
import { BrandCompany, ExternalProduct, Product } from '../../../models/product/types';
import { MFRProject } from '../../../models/project/types';
import {
    getLiveGuidelineByIdAction,
    getLiveGuidelineProductsAction,
} from '../../../redux/actions/liveGuidelineActions';
import {
    LiveGuidelineDetail,
    LiveGuidelineProduct,
} from '../../../redux/actions/liveGuidelineActions/types';
import { getProductReferencesAction } from '../../../redux/actions/productActions';
import { ProductReferences } from '../../../redux/actions/productActions/types';
import { getCompaniesOfProducts, getExternalProductsAction } from '../../../redux/actions/transparencyCatalogActions';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../redux/actions/transparencyCatalogActions/types';
import { addPages, removePages } from '../../../redux/reducers/navigationReducer/NavigationSlice';
import { blueDark01, blueLink } from '../../../styles/colors';
import { EmptyPendingPage, MiddleSpin, Text } from '../../../styles/common.styles';
import {
    MFRPageContainer,
    ReturnToProductTabContainer,
    ReturnToProductTabLink,
} from '../../mfrPage/MFRPage.styles';
import { MFRPProductItemContainer } from '../../mfrPage/mfrProduct/MFRProduct.styles';
import MFRPageCompanyHeader from '../../mfrPage/MFRPageCompanyHeader';

interface Params {
    id: string;
    liveGuidelineProductId: string;
    mode?: string;
}

const LiveGuidelineProductPage: React.FC = () => {
    const params = useParams<Params>();
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.authReducer.user);
    const { sustainabilityDocuments, leedDocumentTypes } = useUploadDocumentTypes();

    const liveGuidelineId = Number(params.id);
    const liveGuidelineProductId = Number(params.liveGuidelineProductId);

    const [guideline, setGuideline] = useState<LiveGuidelineDetail | null>(null);
    const [product, setProduct] = useState<Product | null>(null);
    const [references, setReferences] = useState<ProductReferences | null>(null);
    const [brandInformation, setBrandInformation] = useState<BrandCompany | undefined>(undefined);
    const { isOpen: isHelpOpen, onToggle: toggleHelp } = useModalState();
    const [pending, setPending] = useState(true);

    useEffect(() => {
        loadData();
    }, [liveGuidelineId, liveGuidelineProductId]);

    useEffect(() => {
        return () => {
            dispatch(removePages());
        };
    }, []);

    useEffect(() => {
        dispatch(
            addPages([
                { key: NavigationKey.DASHBOARD, value: `${user?.firstName}’s Dashboard` },
                { key: NavigationKey.LIVE_GUIDELINES, value: 'Live Guidelines' },
                {
                    key: `/live-guidelines/${liveGuidelineId}`,
                    value: guideline?.name || '',
                    image: 'src/assets/images/lightning-charge-fill.svg',
                },
                {
                    key: `/live-guidelines/${liveGuidelineId}/product/${liveGuidelineProductId}`,
                    value: product ? `${product.company} ${product.productName}`.trim() : '',
                },
            ]),
        );
    }, [guideline?.name, product?.productName]);

    const loadData = async () => {
        setPending(true);
        try {
            const [guidelineResult, productsResult, referencesResult] = (await Promise.all([
                dispatch(getLiveGuidelineByIdAction(liveGuidelineId)),
                dispatch(getLiveGuidelineProductsAction(liveGuidelineId)),
                dispatch(getProductReferencesAction()),
            ])) as unknown as [
                ActionResponse<LiveGuidelineDetail>,
                ActionResponse<LiveGuidelineProduct[]>,
                ActionResponse<ProductReferences>,
            ];
            setGuideline(guidelineResult.payload?.data || null);
            setReferences(referencesResult.payload?.data ?? null);

            const guidelineProduct = (productsResult.payload?.data || []).find(
                (p) => p.id === liveGuidelineProductId && p.productId != null,
            );
            if (!guidelineProduct) {
                setProduct(null);
                return;
            }

            const { payload } = (await dispatch(
                getExternalProductsAction({ productIds: [guidelineProduct.productId!] }),
            )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;
            const external = payload?.data ? flattenTcMfSectionProducts(payload.data.mfSection) : [];
            const [merged] = mergeInternalProductsWithExternal(
                [toGuidelineInternalProduct(guidelineProduct)],
                external,
            );
            if (!merged) {
                setProduct(null);
                return;
            }

            const [company] = await getCompaniesOfProducts(
                [merged as unknown as ExternalProduct],
                dispatch,
            );
            // Same as the project page: the manufacturer's spec link for this product's MasterFormat section.
            const specSection = (company as any)?.specs?.find(
                (spec: any) => spec.mfSectionNumber === merged.mfSection?.number,
            );
            if (specSection) {
                merged.specUrl = specSection.specUrl;
            }
            setBrandInformation(company);
            setProduct(merged);
        } finally {
            setPending(false);
        }
    };

    // The upload components derive edit/delete rights from a project role: a guideline owner or
    // Account Admin maps to OWNER (full), any other member to COMMENTER (add only), as the API enforces.
    const canEdit =
        user?.accountRole === AccountPermissionTypes.ACCOUNT_ADMIN ||
        !!guideline?.owners.some((owner) => owner.userId === user?.id);
    const permissionContext = {
        userRole: canEdit ? ProjectPermissionTypes.OWNER : ProjectPermissionTypes.COMMENTER,
    } as MFRProject;

    if (pending) {
        return (
            <MiddleSpin spinning size="large">
                <EmptyPendingPage />
            </MiddleSpin>
        );
    }

    const returnLink = `/live-guidelines/${liveGuidelineId}/1${
        product ? `?productId=${product.productId}` : ''
    }`;

    return (
        <>
            <PageHeader
                title={guideline?.name || ''}
                iconTitleGap={4}
                iconProp={
                    guideline?.name
                        ? { src: 'lightning-charge-fill.svg', style: { width: 24, height: 24 } }
                        : undefined
                }
            />
            <MFRPageContainer>
                <ReturnToProductTabContainer>
                    <ReturnToProductTabLink to={returnLink}>
                        <Image src="src/assets/images/return-arrow.svg" preview={false} />
                        <Text weight={400} height={18} size={14} color={blueLink}>
                            Return to Products tab
                        </Text>
                    </ReturnToProductTabLink>
                </ReturnToProductTabContainer>
                <MFRPageCompanyHeader
                    company={brandInformation}
                    onToggle={toggleHelp}
                    user={user}
                />
                {product && references ? (
                    <MFRPProductItemContainer id={product.id.toString()}>
                        <ProductItemHeader
                            product={product}
                            productsCount={1}
                            showCheckbox={false}
                            showHelp={
                                brandInformation?.contactEmail !== null &&
                                brandInformation?.contactInfo !== null
                            }
                            showHeader
                        />
                        <ProductComments
                            product={product}
                            selectedProductId={product.productId.toString()}
                            openByDefault={params.mode === 'comments'}
                        />
                        <ProductUploads
                            teamMembers={[]}
                            docAndImagesUploads={[]}
                            newSustainabilityDocuments={sustainabilityDocuments}
                            leedDocumentTypes={leedDocumentTypes}
                            allDocumentTypes={references.allDocumentTypes || []}
                            product={product}
                            project={permissionContext}
                            openByDefault={params.mode === 'documents'}
                            uploadType={UploadType.DOCUMENT}
                        />
                        {isFunctionalityEnabled(UPLOAD_PHOTOS) && (
                            <ProductUploads
                                teamMembers={[]}
                                docAndImagesUploads={[]}
                                newSustainabilityDocuments={sustainabilityDocuments}
                                leedDocumentTypes={leedDocumentTypes}
                                allDocumentTypes={references.allDocumentTypes || []}
                                product={product}
                                project={permissionContext}
                                openByDefault={params.mode === 'images'}
                                uploadType={UploadType.PICTURE}
                            />
                        )}
                    </MFRPProductItemContainer>
                ) : (
                    <Text weight={400} height={18} size={14} color={blueDark01}>
                        This product is no longer available in the Live Guideline.
                    </Text>
                )}
            </MFRPageContainer>
            {isHelpOpen && (
                <HelpModal
                    isOpen={isHelpOpen}
                    handleCancel={toggleHelp}
                    companyId={brandInformation?.id || 0}
                />
            )}
        </>
    );
};

export default LiveGuidelineProductPage;
